import React, { useEffect, useMemo, useCallback, useState, lazy } from 'react';
import { Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from './components/Layout/Layout';
import _, { isArray, isEmpty } from 'lodash';
import { Spin, Modal, message } from 'antd';
import { ConfigProvider } from 'antd';
import useCssVariables from './hooks/useCssVariables';
import {
  setIsOtpAuthCheckModalOpen,
  updateConfig,
  setMembershipUser,
} from './store/config';
import { useDispatch, useSelector } from 'react-redux';
import he from 'antd/es/locale/he_IL';
import { RootState } from './store/store';
import apiClient from '@skygroup/shared/API/apiClient';
import appConfigFamily from './appConfigFamilybreak';
import appConfigHotel360 from './appConfigHotel360';
import appConfigIssta from './appConfigIssta';
import appConfigSap from './appConfigSap';
import appConfigIpc from './appConfigIpc';
import appConfigUniversal from './appConfigUniversal';
import appConfigStudents from './appConfigSkystudents';
import appConfigSoho from './appConfigSoho';
import appConfigGuestRoom from './appConfigGuestRoom';
import { ApiStatus } from '@skygroup/shared/components/crud-helpers/ModelApiMessage';
import { updateSearchInitialState } from './modules/SearchDatePicker/searchDatePickerSlice';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './assets/fonts/Heebo/heebo.css';
import endpoints from './utils/endpoints';
import SpinLoader from '@skygroup/shared/components/spin-loader/SpinLoader';
import useSiteLang from './hooks/useSiteLang';
import { useLoadAgentUser } from './hooks/useLoadAgentUser';
import ShoppingCartV2 from './modules/ShoppingCart/ShoppingCartV2';
import ScriptLoader from './utils/definedScripts/scriptLoader';
import AcceptCookies from './components/AcceptCookies/AcceptCookies';
import AccessibilityPlugin from './utils/definedScripts/accessibilityPlugin';
import { FaArrowUp } from 'react-icons/fa';
import appConfigSky360 from './appConfigSky360';
import getImagePathFromName from '@skygroup/shared/utils/getImagePathFromName';
import { useMembershipAuth } from './hooks/useMembershipAuth';
import useHandleMessagesFromBO from './useHandleMessagesFromBO';
import ErrorThrower from '@skygroup/shared/components/ErrorBoundary/ErrorThrower';
import DynamicPage from './components/DynamicPages/DynamicPage';
import DynamicPageEdit from './components/DynamicPages/dynamicPageEdit';
import { setPageConfig } from './store/dynamicPageConfigSlice';
import ScrollToTop from './utils/ScrollToTop';

const queryClient = new QueryClient();

function App() {
  const dispatch = useDispatch();
  const search = useSelector((state: RootState) => state.search);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAgentLoading } = useLoadAgentUser();
  const {
    config,
    agentInfo,
    translations: t,
  } = useSelector((data: RootState) => data.config);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  const [showBackToTopButton, setShowBackToTopButton] = useState(false);
  const [configError, setConfigError] = useState<Error | null>(null);

  const isPreviewMode = search?.searchParams?.prvmd === '1';
  const isEditMode = searchParams.get('editMode') === 'true';
  const getDeparture = (value: any): string => {
    if (!value) return "ALP";

    // Case 1: Array
    if (Array.isArray(value)) {
      return value[0] || "ALP";
    }

    // Case 2: String like "ALP,TBS"
    if (typeof value === "string") {
      const parts = value.split(",").map(v => v.trim());
      return parts[0] || "ALP";
    }

    // Fallback
    return "ALP";
};
  const { isAuthenticated } = useMembershipAuth();

  const getConfig = useCallback(async () => {
    try {
      const response = await apiClient.get(`${endpoints.APP.config}`);

      let appConfig: any = false;

      const host = process.env.REACT_APP_HOST ?? window.location.host;
      if (response.data?.status == ApiStatus.success) {
        appConfig = response.data.data;
      } else {
        if (
          host == 'b2b.sky360.co.il' ||
          host == 'stg-b2c.sky360.co.il' ||
          host == 'b2c.sky360.co.il'
        ) {
          appConfig = appConfigSky360;
        } else if (
          host == 'stg-b2c.familybreak.co.il' ||
          host == 'www.familybreak.co.il'
        ) {
          appConfig = appConfigFamily;
        } else if (host == 'issta.sky360.co.il') {
          appConfig = appConfigIssta;
        } else if (host == 'sap.sky360.co.il') {
          appConfig = appConfigSap;
        } else if (host == 'guestroom.sky360.co.il') {
          appConfig = appConfigGuestRoom;
        } else if (host == 'ipc.sky360.co.il') {
          appConfig = appConfigIpc;
        } else if (host == 'universal.sky360.co.il') {
          appConfig = appConfigUniversal;
        } else if (host == 'www.skystudents.co.il') {
          appConfig = appConfigStudents;
        } else if (host == 'soho.sky360.co.il') {
          appConfig = appConfigSoho;
        } else if (host == 'hotel.sky360.co.il') {
          appConfig = appConfigHotel360;
        }
      }
      // appConfig = appConfigSky360;

      if (!appConfig) {
        console.error('Failed to load config based on domain - ' + host);
        return;
      }
      document.title = appConfig.siteConfig.name;
      dispatch(updateConfig(appConfig));

      try {
        if (appConfig?.siteConfig?.favIcon) {
          let favicon = document.querySelector(
            "link[rel='icon']"
          ) as HTMLLinkElement;
          if (favicon) {
            favicon.href = getImagePathFromName(appConfig?.siteConfig?.favIcon);
          } else {
            const newFavicon = document.createElement('link');
            newFavicon.rel = 'icon';
            newFavicon.href = getImagePathFromName(
              appConfig?.siteConfig?.favIcon
            );
            document.head.appendChild(newFavicon);
          }
        }
      } catch (error) {
        console.error('123 Error fetching favicon:', error);
      }

      const searchParams = new URLSearchParams(window.location.search);
      const fromValue = searchParams.get('from');
      const toValue = searchParams.get('to');

dispatch(
  updateSearchInitialState({
    from: fromValue || appConfig.siteConfig.defaultDate?.from,
    range: appConfig.siteConfig.defaultDate?.range,
    destination: appConfig.siteConfig.defaultDestination,
    destinationCity: appConfig.siteConfig.defaultDestinationCity,
    departure: getDeparture(appConfig.siteConfig.defaultDeparture),
    departureCity: appConfig.siteConfig.defaultDepartureCity,
  })
);

      ConfigProvider.config({
        theme: {
          primaryColor: appConfig.theme.primary,
        },
      });
      setIsConfigLoaded(true);
    } catch (error) {
      console.error('App config failed to load:', error);
      setConfigError(
        error instanceof Error
          ? error
          : new Error('Failed to load app configuration')
      );
    }
  }, [dispatch]);
  const { isTranLoaded, direction } = useSiteLang({
    siteConfig: config?.siteConfig,
  });
  useEffect(() => {
    getConfig();
  }, []);

  const { selectedInstance, setSelectedInstance } = useHandleMessagesFromBO();

  useEffect(() => {
    if (!isTranLoaded || !isConfigLoaded) {
      return;
    }
    const membershipUserString = localStorage.getItem('membershipUser');
    if (membershipUserString) {
      try {
        const membershipUserObj = JSON.parse(membershipUserString);
        const expirationDate = new Date(membershipUserObj.expirationDate);
        const now = new Date();
        if (expirationDate <= now) {
          localStorage.removeItem('membershipUser');
          localStorage.removeItem('lastMessageShownTimestamp');
          return;
        }
        const { data, user_name } = membershipUserObj;
        dispatch(setMembershipUser({ data, user_name }));

        const lastMessageShownString = localStorage.getItem(
          'lastMessageShownTimestamp'
        );
        const twoHours = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
        if (!lastMessageShownString) {
          message.success(`${user_name} ${t.auth.loginSuccess}`);
          localStorage.setItem('lastMessageShownTimestamp', now.toISOString());
        } else {
          const lastMessageShown = new Date(lastMessageShownString);
          if (isNaN(lastMessageShown.getTime())) {
            message.success(`${user_name} ${t.auth.loginSuccess}`);
            localStorage.setItem(
              'lastMessageShownTimestamp',
              now.toISOString()
            );
          } else {
            const timeDifference = now.getTime() - lastMessageShown.getTime();
            if (timeDifference >= twoHours) {
              message.success(`${user_name} ${t.auth.loginSuccess}`);
              localStorage.setItem(
                'lastMessageShownTimestamp',
                now.toISOString()
              );
            }
          }
        }
      } catch (error) {
        console.log(error);
      }
    }
  }, [dispatch, isTranLoaded, isConfigLoaded, t]);

  useEffect(() => {
    if (
      isConfigLoaded &&
      config?.siteConfig?.membership?.auth_type === 'org_istadrut' &&
      !isAuthenticated() &&
      location.pathname !== '/order/manage'
    ) {
      dispatch(setIsOtpAuthCheckModalOpen(true));
    }
  }, [isConfigLoaded, config, isAuthenticated, location, dispatch]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 6000) {
        setShowBackToTopButton(true);
      } else {
        setShowBackToTopButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const generateCssVariables = useCssVariables();

  let topClass = config?.siteConfig?.style?.topClass
    ? config?.siteConfig?.style?.topClass
    : '';

  if (agentInfo?.agentMode) {
    topClass += ' agent-mode';
  }

  return (
    <ErrorThrower error={configError}>
      <SpinLoader
        isLoading={!(isConfigLoaded && isTranLoaded) || isAgentLoading}
      >
        <QueryClientProvider client={queryClient}>
          <div
            className={`App direction-${direction} ${topClass}`}
            id="App"
            dir={direction}
          >
            <ScrollToTop />
            {generateCssVariables('body', config?.theme)}
            {!(
              isPreviewMode ||
              isEditMode ||
              location.pathname.split('/')[1] === 'checkout' ||
              location.pathname === '/checkout' ||
              location.pathname.split('/')[1] === 'order' ||
              location.pathname === '/order'
            ) && <ShoppingCartV2 pageType="rest" />}

            {showBackToTopButton && (
              <div
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="back-to-top"
              >
                <FaArrowUp />
              </div>
            )}

            <ConfigProvider direction={direction} locale={he}>
              <Routes>
                      <Route
                        key={'dynamicPagetype1'}
                        path={"prm/:secret"}
                        element={
                          <DynamicPage
                            isPreviewMode={isPreviewMode}
                            selectedInstance={selectedInstance}
                            setSelectedInstance={setSelectedInstance}
                            type={"promotion"}
                          />
                        }
                      />
                      <Route
                        key={'dynamicPagetype2'}
                        path={"dst/:secret"}
                        element={
                          <DynamicPage
                            isPreviewMode={isPreviewMode}
                            selectedInstance={selectedInstance}
                            setSelectedInstance={setSelectedInstance}
                            type={"destination"}
                          />
                        }
                      />
                      <Route
                        key={'dynamicPagetype3'}
                        path={"page/:secret"}
                        element={
                          <DynamicPage
                            isPreviewMode={isPreviewMode}
                            selectedInstance={selectedInstance}
                            setSelectedInstance={setSelectedInstance}
                            type={"general"}
                          />
                        }
                      />
                      <Route
                        key={'dynamicPageEdit'}
                        path={"dynamicPageEdit"}
                        element={
                          <DynamicPageEdit
                            isPreviewMode={isPreviewMode}
                            selectedInstance={selectedInstance}
                            setSelectedInstance={setSelectedInstance}
                          />
                        }
                      />
                {config?.pages?.map((page, i) => {
                  const pathArray: string[] = isArray(page.path)
                    ? page.path
                    : [page.path];
                  return pathArray.map((path, index) => {
                    return (
                      <Route
                        key={index + path}
                        path={page.name === 'NotFound404' ? '*' : path}
                        element={
                          <Layout
                            pageId={page.id || page.name}
                            pagePath={path}
                            pageComponents={page.modules}
                            layout={page.layout}
                            isPreviewMode={isPreviewMode}
                            selectedInstance={selectedInstance}
                            setSelectedInstance={setSelectedInstance}
                            page={page}
                          />
                        }
                      />
                    );
                  });
                })}
              </Routes>
              {isEmpty(config) && <Spin className="center" size="large" />}
            </ConfigProvider>
          </div>
          {!isEditMode && config?.siteConfig?.accessibility?.siteKey ? (
            <AccessibilityPlugin
              siteKey={config?.siteConfig?.accessibility?.siteKey}
            />
          ) : null}
          <AcceptCookies />
        </QueryClientProvider>
      </SpinLoader>
    </ErrorThrower>
  );
}

export default App;
